import type { AxiosError } from 'axios';
import { z } from 'zod/v4';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { api } from '../../api';
import type { ErrorMessage } from '../../models/Error';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';

const { logError } = RaygunErrorHandlerService();

const SupportResourceSchema = z.object({
  id: z.uuidv4(),
  name: z.string(),
  description: z.string(),
  phone_number: z.string().nullable(),
  website: z.string().nullable(),
  subjects: z.array(z.string()),
  org_names: z.array(z.string()),
});

export type SupportResource = z.infer<typeof SupportResourceSchema>;

type AdvocateResourceSliceType = {
  resources: SupportResource[];
  allUniqueSubjects: string[];
  allUniqueOrgs: string[];
  loading: boolean;
};

const initialState: AdvocateResourceSliceType = {
  resources: [],
  allUniqueSubjects: [],
  allUniqueOrgs: [],
  loading: false,
};

export const getAdvocateResources = createAsyncThunk<SupportResource[], undefined, AsyncThunkConfig>(
  'advocateResource/getAdvocateResources',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<SupportResource[]>('/advocate/v0_advocate_resources');
      const result = z.array(SupportResourceSchema).safeParse(response);
      if (!result.success) {
        logError(result.error, ['advocateResourceSlice', 'getAdvocateResources', 'parsing']);
      }
      return response ?? [];
    } catch (e) {
      logError(e, ['advocateResourceSlice', 'getAdvocateResources']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const advocateResourceSlice = createSlice({
  name: 'advocateResource',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAdvocateResources.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(getAdvocateResources.fulfilled, (state, action) => {
        state.resources = action.payload;
        state.allUniqueSubjects = [...new Set(action.payload.flatMap((r) => r.subjects))].sort();
        state.allUniqueOrgs = [...new Set(action.payload.flatMap((r) => r.org_names))].sort();
        state.loading = false;
      })
      .addCase(getAdvocateResources.rejected, (state, _action) => {
        state.loading = false;
      });
  },
});
